import React, { useState, useMemo, useCallback } from "react";

const toTitleCase = (str: string): string =>
  str
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const normalizeTimingKey = (timing?: string): string => {
  const key = String(timing || "").toLowerCase();
  if (key === "pre_event" || key === "pre_match") return "pre_event";
  if (key === "post_event" || key === "post_match") return "post_event";
  return key || "single";
};

const resolvePaymentTiming = (payment: any): string =>
  normalizeTimingKey(
    payment?.paymentTiming ||
      payment?.payment_timing ||
      payment?.component?.paymentTiming ||
      payment?.component?.payment_timing,
  );

type TemplatePaymentLabels = {
  fieldLabels: Record<string, string>;
  optionLabels: Record<string, Record<string, string>>;
};

const emptyTemplatePaymentLabels: TemplatePaymentLabels = {
  fieldLabels: {},
  optionLabels: {},
};

const getTemplateStepId = (step: any): string =>
  String(
    step?.stepId ||
      step?.id ||
      step?.key ||
      String(step?.title || "step")
        .toLowerCase()
        .replace(/\s+/g, "_"),
  );

const getOptionValue = (option: any): string => {
  if (typeof option === "string") return option;
  return String(option?.value ?? option?.key ?? option?.id ?? "");
};

const getOptionText = (option: any): string => {
  if (typeof option === "string") return option;
  return String(
    option?.label ??
      option?.title ??
      option?.text ??
      option?.name ??
      option?.value ??
      option?.key ??
      "",
  );
};

const addOptionLabel = (
  labels: TemplatePaymentLabels,
  fieldPath: string,
  value: string,
  label: string,
) => {
  if (!value || !label) return;
  if (!labels.optionLabels[fieldPath]) labels.optionLabels[fieldPath] = {};
  labels.optionLabels[fieldPath][value] = label;
  labels.optionLabels[fieldPath][value.toLowerCase()] = label;
  labels.optionLabels[fieldPath][value.toUpperCase()] = label;
};

const buildTemplatePaymentLabels = (templateJson?: any): TemplatePaymentLabels => {
  const steps = Array.isArray(templateJson?.steps) ? templateJson.steps : [];
  if (steps.length === 0) return emptyTemplatePaymentLabels;

  const labels: TemplatePaymentLabels = {
    fieldLabels: {},
    optionLabels: {},
  };

  const registerField = (field: any, fieldPath: string, stepId: string) => {
    const fieldLabel = field?.label ?? field?.title ?? field?.name;
    if (fieldLabel) labels.fieldLabels[fieldPath] = String(fieldLabel);

    if (Array.isArray(field?.options)) {
      field.options.forEach((option: any) => {
        addOptionLabel(labels, fieldPath, getOptionValue(option), getOptionText(option));
      });
    }

    if (Array.isArray(field?.fields)) {
      field.fields.forEach((nestedField: any) => {
        const nestedKey = String(nestedField?.key || "");
        if (!nestedKey) return;
        registerField(nestedField, `${fieldPath}.${nestedKey}`, stepId);
      });
    }
  };

  steps.forEach((step: any) => {
    const stepId = getTemplateStepId(step);
    if (!Array.isArray(step?.fields)) return;

    step.fields.forEach((field: any) => {
      const fieldKey = String(field?.key || "");
      if (!fieldKey) return;
      registerField(field, `${stepId}.${fieldKey}`, stepId);
    });
  });

  return labels;
};

interface BreakdownItem {
  componentType: string;
  calculationMode: string;
  rawValue: number;
  resolvedAmount: number;
}

interface ComponentPaymentsTabProps {
  deal: any;
  formData: any;
  isFighter: boolean;
  canPayComponents: boolean;
  handlePayComponent: (componentPaymentId: number) => void | Promise<void>;
  t: any;
}

const ComponentPaymentsTab: React.FC<ComponentPaymentsTabProps> = ({
  deal,
  formData,
  isFighter,
  canPayComponents,
  handlePayComponent,
  t,
}) => {
  const [expandedTimings, setExpandedTimings] = useState<Record<string, boolean>>({});
  const [hoveredInfo, setHoveredInfo] = useState<string | null>(null);
  const [loadingPayments, setLoadingPayments] = useState<Record<number, boolean>>({});

  const templateLabels = useMemo(
    () => buildTemplatePaymentLabels(deal?.templateJson),
    [deal?.templateJson],
  );

  const getTemplateOptionLabel = useCallback(
    (fieldPath: string, value?: string | null): string | undefined => {
      const normalizedValue = String(value || "");
      if (!normalizedValue) return undefined;

      const fieldLabels = templateLabels.optionLabels[fieldPath];
      return (
        fieldLabels?.[normalizedValue] ||
        fieldLabels?.[normalizedValue.toLowerCase()] ||
        fieldLabels?.[normalizedValue.toUpperCase()]
      );
    },
    [templateLabels],
  );

  const paymentsByTiming = useMemo(() => {
    const timingMap: Record<string, any[]> = {};
    (deal?.componentPayments || []).forEach((payment: any) => {
      const timing = resolvePaymentTiming(payment);
      if (!timingMap[timing]) timingMap[timing] = [];
      timingMap[timing].push(payment);
    });
    return timingMap;
  }, [deal?.componentPayments]);

  const paymentTimingsToRender = useMemo(() => {
    const availableTimings = Object.keys(paymentsByTiming);

    if (availableTimings.length > 0) {
      const hasPreEvent = availableTimings.includes("pre_event");
      const hasPostEvent = availableTimings.includes("post_event");
      if (hasPreEvent && hasPostEvent) return ["pre_event", "post_event"];
      return availableTimings;
    }

    const paymentType = (deal?.paymentType || "").toUpperCase();
    if (paymentType === "SPLIT") return ["pre_event", "post_event"];
    if (paymentType === "UPFRONT") return ["pre_event"];

    return ["single"];
  }, [paymentsByTiming, deal?.paymentType]);

  const isSplitPayment = paymentTimingsToRender.length > 1;

  const getTimingLabel = (timingKey: string): string => {
    const templateTimingLabel = getTemplateOptionLabel(
      "components.fundingStage",
      timingKey,
    );
    if (!isSplitPayment) return t("payment") || "Payment";
    if (timingKey === "pre_event") {
      return templateTimingLabel || t("preEvent") || "Pre-Event";
    }
    if (timingKey === "post_event") {
      return templateTimingLabel || t("postEvent") || "Post-Event";
    }
    return toTitleCase(timingKey);
  };

  const getBreakdownItems = (payment: any, timingKey: string): BreakdownItem[] => {
    if (!payment) return [];
    if (timingKey === "pre_event" || timingKey === "single") {
      return payment.preMatchBreakdown || [];
    }
    if (timingKey === "post_event") {
      return payment.postMatchBreakdown || [];
    }
    return payment.preMatchBreakdown || payment.postMatchBreakdown || [];
  };

  const getCalculationModeLabel = (mode: string, rawValue: number): string => {
    if (mode === "PERCENT_OF_BASE") {
      return t("percentOfBase", { value: rawValue }) || `${rawValue}% of Base`;
    }
    if (mode === "FIXED") {
      return getTemplateOptionLabel("components.mode", mode) || t("fixed") || "Fixed";
    }
    const templateModeLabel = getTemplateOptionLabel("components.mode", mode);
    if (templateModeLabel) return templateModeLabel;
    return toTitleCase(mode);
  };

  const getComponentTypeLabel = (componentType: string): string => {
    if (componentType === "BASE_AMOUNT") {
      return templateLabels.fieldLabels["core.baseAmount"] || t("baseAmount") || "Base Amount";
    }

    return (
      getTemplateOptionLabel("components.componentType", componentType) ||
      toTitleCase(componentType)
    );
  };

  const handlePayClick = useCallback(
    async (paymentId: number) => {
      if (loadingPayments[paymentId]) return;

      try {
        setLoadingPayments((prev) => ({ ...prev, [paymentId]: true }));
        await Promise.resolve(handlePayComponent(paymentId));
      } finally {
        setLoadingPayments((prev) => ({ ...prev, [paymentId]: false }));
      }
    },
    [handlePayComponent, loadingPayments],
  );

  return (
    <div className="space-y-3">
      {paymentTimingsToRender.map((timingKey) => {
        const timingDisplayLabel = getTimingLabel(timingKey);
        const paymentsForTiming = paymentsByTiming[timingKey] || [];
        const isExpanded = !!expandedTimings[timingKey];

        const totalAmount = paymentsForTiming.reduce(
          (sum: number, payment: any) => sum + Number(payment.amount || 0),
          0,
        );

        const hasPendingPayments = paymentsForTiming.some(
          (payment: any) => payment.status === "PENDING",
        );

        const hasOnlyPaid =
          paymentsForTiming.length > 0 &&
          paymentsForTiming.every(
            (payment: any) =>
              payment.status === "PAID" || payment.status === "COMPLETED",
          );

        const unpaidPayments = paymentsForTiming.filter(
          (payment: any) => payment.status === "PENDING",
        );

        const hasCancelledPayments = paymentsForTiming.some(
          (payment: any) => payment.status === "CANCELLED",
        );

        const canShowPayButton =
          hasPendingPayments && !isFighter && canPayComponents;

        return (
          <div
            key={timingKey}
            className={`rounded-xl border transition-all duration-200 bg-white ${
              isExpanded ? "border-gray-300 shadow-sm" : "border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between px-4 py-3.5 gap-3">
              <button
                onClick={() =>
                  setExpandedTimings((prev) => ({
                    ...prev,
                    [timingKey]: !prev[timingKey],
                  }))
                }
                className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-70 transition-opacity"
                aria-expanded={isExpanded}
              >
                <div className="min-w-0 flex flex-col gap-0.5">
                  <div className="flex items-center gap-1 relative">
                    <span className="text-sm font-semibold text-gray-800">
                      {timingDisplayLabel}
                    </span>

                    {timingKey === "post_event" && (
                      <div
                        className="relative flex items-center"
                        onMouseEnter={() => setHoveredInfo(timingKey)}
                        onMouseLeave={() => setHoveredInfo(null)}
                      >
                        <svg
                          className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                          />
                        </svg>

                        {hoveredInfo === timingKey && (
                          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md shadow-lg z-50">
                            {t("postEventPaymentNote") ||
                              "Note: As per the result policies, the amount will change."}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {paymentsForTiming[0]?.dueOn && (
                    <span className="text-[11px] text-gray-400">
                      {t("dueDate") || "Due Date"}: {paymentsForTiming[0].dueOn}
                    </span>
                  )}
                </div>
              </button>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                  {totalAmount} {formData?.core?.currency || ""}
                </span>

                {hasPendingPayments && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-200">
                    {t("paymentStatusPending") || "Pending"}
                  </span>
                )}

                {hasCancelledPayments && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 ring-1 ring-red-200">
                    {t("paymentStatusCancelled") || "Cancelled"}
                  </span>
                )}

                {hasOnlyPaid && paymentsForTiming.length > 0 && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
                    {t("paymentStatusPaid") || "Paid"}
                  </span>
                )}

                {canShowPayButton &&
                  unpaidPayments.map((unpaidPayment: any) => {
                    const isLoading = !!loadingPayments[unpaidPayment.id];

                    return (
                      <button
                        key={unpaidPayment.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handlePayClick(unpaidPayment.id);
                        }}
                        disabled={isLoading}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-all flex items-center justify-center gap-2 ${
                          isLoading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-emerald-500 hover:bg-emerald-600 active:scale-95"
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <svg
                              className="animate-spin h-3 w-3"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8z"
                              />
                            </svg>
                            {t("processing") || "Processing..."}
                          </>
                        ) : (
                          t("Pay") || "Pay"
                        )}
                      </button>
                    );
                  })}

                <button
                  onClick={() =>
                    setExpandedTimings((prev) => ({
                      ...prev,
                      [timingKey]: !prev[timingKey],
                    }))
                  }
                  className={`w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 transition-transform duration-200 flex-shrink-0 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                  aria-label={
                    isExpanded
                      ? t("collapse") || "Collapse"
                      : t("expand") || "Expand"
                  }
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 4L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/40">
                {paymentsForTiming.length > 0 ? (
                  paymentsForTiming.map((payment: any) => {
                    const breakdownItems = getBreakdownItems(payment, timingKey);

                    return (
                      <div key={payment.id} className="space-y-2">
                        {breakdownItems.length > 0 ? (
                          breakdownItems.map((item: BreakdownItem, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3.5 py-2.5"
                            >
                              <div className="flex flex-col gap-0.5">
                                <div className="text-sm font-medium text-gray-800">
                                  {getComponentTypeLabel(item.componentType)}
                                </div>
                                <div className="text-[11px] text-gray-400">
                                  {getCalculationModeLabel(
                                    item.calculationMode,
                                    item.rawValue,
                                  )}
                                </div>
                              </div>
                              <div className="text-sm font-semibold text-gray-900 tabular-nums">
                                {item.resolvedAmount} {formData?.core?.currency || ""}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg bg-white">
                            {t("noBreakdownAvailable") || "No breakdown available"}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg bg-white">
                    {t("noPaymentsForTiming") || "No payments for this timing"}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {(!deal.componentPayments || deal.componentPayments.length === 0) && (
        <div className="text-sm text-gray-400 text-center py-8 border border-dashed border-gray-200 rounded-xl">
          {t("noComponentPayments") || "No component payments"}
        </div>
      )}
    </div>
  );
};

export default ComponentPaymentsTab;
